import { useState } from 'react';

export interface PageFinalCTAProps {
  heading: string;
  headingItalic?: string;
  subheading?: string;
  urgency?: string;
  formUrl: string;
  imageSrc: string;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function PageFinalCTA({
  heading,
  headingItalic,
  subheading,
  urgency,
  formUrl,
  imageSrc,
}: PageFinalCTAProps) {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) return;
    setStatus('submitting');
    const data = new URLSearchParams();
    new FormData(form).forEach((v, k) => data.append(k, v as string));
    try {
      const res = await fetch(formUrl, {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="relative overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img src={imageSrc} alt="Yarnell Emporium" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-inkblack/92" />
      </div>

      <div className="relative z-10">
        {/* Top info strip — no pulse animation */}
        <div className="border-b border-white/8 px-8 lg:px-12 py-5">
          <div className="max-w-screen-xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rotate-45 border border-antique flex-shrink-0" />
              <span className="text-antique text-xs font-sans font-500 tracking-[0.22em] uppercase">
                {urgency || 'One-of-a-kind items — inventory changes weekly'}
              </span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="tel:9284279593" className="text-white/40 hover:text-antique text-xs font-sans tracking-wide cursor-pointer">
                928.427-9593
              </a>
              <span className="w-px h-4 bg-white/15" />
              <span className="text-white/40 text-xs font-sans tracking-wide">Open Daily 9am – 5pm</span>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="max-w-screen-xl mx-auto px-8 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            {/* Left: Editorial copy */}
            <div>
              <h2
                className="font-serif text-ivory mb-8 leading-none"
                style={{ fontSize: 'clamp(2.8rem, 6vw, 5.5rem)', letterSpacing: '-0.025em', fontWeight: 700 }}
              >
                {heading}
                {headingItalic && (
                  <span className="block italic text-antique font-400" style={{ lineHeight: 1.1 }}>
                    {headingItalic}
                  </span>
                )}
              </h2>

              {/* Ornamental divider */}
              <div className="flex items-center gap-4 mb-8">
                <span className="w-16 h-px bg-antique/50" />
                <span className="w-2 h-2 rotate-45 border border-antique/50" />
                <span className="w-8 h-px bg-antique/30" />
              </div>

              {subheading && (
                <p className="text-ivory/50 text-sm lg:text-base font-sans font-300 leading-relaxed mb-12 max-w-md">
                  {subheading}
                </p>
              )}

              <div className="flex flex-wrap gap-4 mb-12">
                <a
                  href="https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-antique text-inkblack text-xs font-sans font-700 tracking-[0.18em] uppercase px-10 py-4 whitespace-nowrap hover:bg-white cursor-pointer"
                >
                  Visit the Store
                </a>
                <a
                  href="tel:9284279593"
                  className="border border-white/35 text-white text-xs font-sans font-600 tracking-[0.16em] uppercase px-8 py-4 whitespace-nowrap hover:border-antique hover:text-antique cursor-pointer"
                >
                  <i className="ri-phone-line mr-2" />
                  Call Now
                </a>
                <a
                  href="https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-white/35 text-white text-xs font-sans font-600 tracking-[0.16em] uppercase px-8 py-4 whitespace-nowrap hover:border-antique hover:text-antique cursor-pointer"
                >
                  <i className="ri-map-pin-line mr-2" />
                  Directions
                </a>
              </div>

              {/* Store info strip */}
              <div className="flex flex-wrap gap-6 pt-8 border-t border-white/10">
                <div>
                  <p className="text-ivory/25 text-[10px] font-sans tracking-[0.2em] uppercase mb-1">Address</p>
                  <p className="text-ivory/60 text-xs font-sans">22899 S Hwy 89, Yarnell, AZ 85362</p>
                </div>
                <div>
                  <p className="text-ivory/25 text-[10px] font-sans tracking-[0.2em] uppercase mb-1">Hours</p>
                  <p className="text-ivory/60 text-xs font-sans">Open Daily — 9:00 AM to 5:00 PM</p>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div>
              <div className="border border-white/12 p-8 lg:p-10 bg-inkblack/60">
                <div className="mb-7">
                  <h3 className="font-serif text-ivory text-2xl lg:text-3xl font-700 mb-2 leading-snug">
                    Stay in the Loop
                  </h3>
                  <p className="text-ivory/40 text-xs font-sans font-300 leading-relaxed">
                    Be first to know when new antiques, furniture &amp; unique finds arrive in-store.
                  </p>
                </div>

                {status === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-14 h-14 border border-antique flex items-center justify-center mx-auto mb-5">
                      <i className="ri-checkbox-circle-line text-antique text-2xl" />
                    </div>
                    <p className="font-serif text-ivory text-xl font-600 mb-2">You&apos;re on the list!</p>
                    <p className="text-ivory/40 text-xs font-sans font-300">We&apos;ll notify you when new finds arrive.</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    action={formUrl}
                    method="POST"
                    id="new-arrivals-form"
                    className="flex flex-col gap-3"
                  >
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name"
                      required
                      className="bg-white/5 border border-white/12 text-ivory text-sm font-sans px-4 py-3.5 placeholder-ivory/25 focus:outline-none focus:border-antique w-full"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      required
                      className="bg-white/5 border border-white/12 text-ivory text-sm font-sans px-4 py-3.5 placeholder-ivory/25 focus:outline-none focus:border-antique w-full"
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone (optional)"
                      className="bg-white/5 border border-white/12 text-ivory text-sm font-sans px-4 py-3.5 placeholder-ivory/25 focus:outline-none focus:border-antique w-full"
                    />
                    <select
                      name="interested_in"
                      required
                      className="bg-white/5 border border-white/12 text-ivory text-sm font-sans px-4 py-3.5 focus:outline-none focus:border-antique w-full cursor-pointer appearance-none"
                    >
                      <option value="" className="bg-inkblack">Interested In...</option>
                      <option value="Antiques" className="bg-inkblack">Antiques &amp; Vintage</option>
                      <option value="Furniture" className="bg-inkblack">Furniture</option>
                      <option value="Gifts" className="bg-inkblack">Gifts &amp; Home Décor</option>
                      <option value="Apparel" className="bg-inkblack">Boutique Apparel</option>
                      <option value="Everything" className="bg-inkblack">Everything!</option>
                    </select>
                    <div className="relative">
                      <textarea
                        name="message"
                        placeholder="Looking for anything specific? (optional)"
                        rows={3}
                        maxLength={500}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="bg-white/5 border border-white/12 text-ivory text-sm font-sans px-4 py-3.5 placeholder-ivory/25 focus:outline-none focus:border-antique w-full resize-none"
                      />
                      <span className="absolute bottom-2 right-3 text-ivory/20 text-xs">{charCount}/500</span>
                    </div>
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="bg-antique text-inkblack text-xs font-sans font-700 tracking-[0.18em] uppercase py-4 hover:bg-white cursor-pointer disabled:opacity-60 whitespace-nowrap mt-1"
                    >
                      {status === 'submitting' ? 'Sending...' : 'Get New Arrival Updates'}
                    </button>
                    {status === 'error' && (
                      <p className="text-red-400 text-xs font-sans text-center">Something went wrong. Please try again.</p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
